import { useState } from 'react'
import toast from 'react-hot-toast'
import {
    useAdminShipping,
    useCreateShipping,
    useUpdateShipping,
    useDeleteShipping,
} from '@/api/useAdminCatalog'
import { Spinner } from '@/components/ui'
import type { ShippingConfig } from '@/api/useShippingConfig'
import styles from './AdminShipping.module.css'

const DEPARTMENT_LABELS: Record<string, string> = {
    amazonas: 'Amazonas', ancash: 'Áncash', apurimac: 'Apurímac',
    arequipa: 'Arequipa', ayacucho: 'Ayacucho', cajamarca: 'Cajamarca',
    callao: 'Callao', cusco: 'Cusco', huancavelica: 'Huancavelica',
    huanuco: 'Huánuco', ica: 'Ica', junin: 'Junín',
    la_libertad: 'La Libertad', lambayeque: 'Lambayeque', lima: 'Lima',
    loreto: 'Loreto', madre_de_dios: 'Madre de Dios', moquegua: 'Moquegua',
    pasco: 'Pasco', piura: 'Piura', puno: 'Puno',
    san_martin: 'San Martín', tacna: 'Tacna', tumbes: 'Tumbes',
    ucayali: 'Ucayali',
}

interface ShippingForm {
    department: string
    surcharge: string
    estimated_days: number
    is_active: boolean
}

const EMPTY_FORM: ShippingForm = {
    department: '',
    surcharge: '',
    estimated_days: 1,
    is_active: true,
}

export function AdminShipping() {
    const { data: configs, isLoading } = useAdminShipping()
    const createShipping = useCreateShipping()
    const updateShipping = useUpdateShipping()
    const deleteShipping = useDeleteShipping()

    const [showAddRow, setShowAddRow] = useState(false)
    const [addForm, setAddForm] = useState<ShippingForm>(EMPTY_FORM)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editForm, setEditForm] = useState<ShippingForm>(EMPTY_FORM)

    const handleAddSave = () => {
        createShipping.mutate(addForm, {
            onSuccess: () => {
                toast.success('Configuración de envío creada')
                setShowAddRow(false)
                setAddForm(EMPTY_FORM)
            },
            onError: () => toast.error('Error al crear configuración de envío'),
        })
    }

    const handleEditStart = (config: ShippingConfig) => {
        setEditingId(config.id)
        setEditForm({
            department: config.department,
            surcharge: config.surcharge,
            estimated_days: config.estimated_days,
            is_active: config.is_active,
        })
    }

    const handleEditSave = (id: number) => {
        updateShipping.mutate(
            { id, ...editForm },
            {
                onSuccess: () => {
                    toast.success('Configuración actualizada')
                    setEditingId(null)
                },
                onError: () => toast.error('Error al actualizar configuración'),
            }
        )
    }

    const handleDelete = (id: number, department: string) => {
        if (!window.confirm(`¿Eliminar la configuración de envío para "${department}"?`)) return
        deleteShipping.mutate(id, {
            onSuccess: () => toast.success('Configuración eliminada'),
            onError: () => toast.error('Error al eliminar configuración'),
        })
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Configuración de Envíos</h1>
                <button
                    className={styles.addButton}
                    onClick={() => {
                        setShowAddRow(true)
                        setAddForm(EMPTY_FORM)
                        setEditingId(null)
                    }}
                >
                    + Agregar Envío
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loading}>
                    <Spinner size="lg" />
                </div>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Departamento</th>
                            <th>Recargo (S/)</th>
                            <th>Días estimados</th>
                            <th>Activo</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showAddRow && (
                            <tr className={styles.formRow}>
                                <td>
                                    <select
                                        className={styles.formInput}
                                        value={addForm.department}
                                        onChange={(e) => setAddForm({ ...addForm, department: e.target.value })}
                                    >
                                        <option value="">Seleccionar</option>
                                        {Object.entries(DEPARTMENT_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={styles.formInput}
                                        value={addForm.surcharge}
                                        onChange={(e) => setAddForm({ ...addForm, surcharge: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className={styles.formInput}
                                        value={addForm.estimated_days}
                                        onChange={(e) => setAddForm({ ...addForm, estimated_days: Number(e.target.value) })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className={styles.formCheckbox}
                                        checked={addForm.is_active}
                                        onChange={(e) => setAddForm({ ...addForm, is_active: e.target.checked })}
                                    />
                                </td>
                                <td>
                                    <div className={styles.actions}>
                                        <button
                                            className={styles.saveBtn}
                                            onClick={handleAddSave}
                                            disabled={createShipping.isPending}
                                        >
                                            Guardar
                                        </button>
                                        <button
                                            className={styles.cancelBtn}
                                            onClick={() => setShowAddRow(false)}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!configs || configs.length === 0 ? (
                            <tr>
                                <td colSpan={5} className={styles.empty}>
                                    No hay configuraciones de envío registradas
                                </td>
                            </tr>
                        ) : (
                            configs.map((config) =>
                                editingId === config.id ? (
                                    <tr key={config.id} className={styles.formRow}>
                                        <td>
                                            <select
                                                className={styles.formInput}
                                                value={editForm.department}
                                                onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                                            >
                                                {Object.entries(DEPARTMENT_LABELS).map(([value, label]) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={styles.formInput}
                                                value={editForm.surcharge}
                                                onChange={(e) => setEditForm({ ...editForm, surcharge: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className={styles.formInput}
                                                value={editForm.estimated_days}
                                                onChange={(e) => setEditForm({ ...editForm, estimated_days: Number(e.target.value) })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className={styles.formCheckbox}
                                                checked={editForm.is_active}
                                                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                            />
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.saveBtn}
                                                    onClick={() => handleEditSave(config.id)}
                                                    disabled={updateShipping.isPending}
                                                >
                                                    Guardar
                                                </button>
                                                <button
                                                    className={styles.cancelBtn}
                                                    onClick={() => setEditingId(null)}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={config.id}>
                                        <td>{DEPARTMENT_LABELS[config.department] || config.department}</td>
                                        <td>S/ {config.surcharge}</td>
                                        <td>{config.estimated_days}</td>
                                        <td>
                                            <span className={`${styles.badge} ${config.is_active ? styles.badgeActive : styles.badgeInactive}`}>
                                                {config.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => handleEditStart(config)}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => handleDelete(config.id, config.department)}
                                                    disabled={deleteShipping.isPending}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            )
                        )}
                    </tbody>
                </table>
            )}
        </div>
    )
}
